import { UpdateIcon } from "@radix-ui/react-icons";
import { QueryKey } from "@tanstack/react-query";

import { RefreshIconButton } from "./AdminPage.styles";
import { useRefreshQueries } from "./useRefreshQueries";

interface IRefreshButtonProps {
  /** Key prefixes of the queries this control re-runs (see `useRefreshQueries`). */
  queryKeys?: readonly QueryKey[];
  /**
   * Hand in the state from `useRefreshQueries` when something else (the
   * table wrapper) also needs to know a refresh is running. Left out, the
   * button manages its own from `queryKeys`.
   */
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

/**
 * The one Refresh control for every /admin table, rendered by the shared
 * `TableWrapper` at the top-right of each table.
 *
 * It re-runs only the table's own queries (the key prefixes it is given), so a
 * table in a modal does not refetch the page behind it and the rest of the
 * app is left alone. While the run is in flight the icon spins and the
 * control is disabled and `aria-busy`. With no keys and no `onRefresh`, or
 * outside a `QueryClientProvider`, it renders nothing rather than throwing.
 */
export function RefreshButton({
  queryKeys,
  onRefresh,
  isRefreshing,
}: IRefreshButtonProps) {
  const own = useRefreshQueries(queryKeys);
  if (!onRefresh && !own.isAvailable) return null;

  const busy = isRefreshing ?? own.isRefreshing;
  const handleClick = onRefresh ?? own.refresh;

  return (
    <RefreshIconButton
      type="button"
      aria-label="Refresh"
      title="Refresh"
      aria-busy={busy}
      disabled={busy}
      spinning={busy}
      onClick={handleClick}
    >
      <UpdateIcon aria-hidden />
    </RefreshIconButton>
  );
}
